import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApiQuery } from "../../../hooks/use-api-query";
import { IncomeTaxReport } from "../../../interfaces/income_tax_report";
import { Alert, Card } from "../../ui";
import Dropdown from "../../ui/Dropdown";
import SvgArrow from "../../ui/icons/arrow";
import Page from "../page";
import InstructionsBoard from "./components/instructions_board";
import SelectStatus, { StatusValue } from "../../ui/select-status";

type FetchYears = {
  Opportunity?: number[];
  Debenture?: number[];
};

const investableTypes = [
  {
    name: "Oportunidades",
    value: "Opportunity",
  },
  {
    name: "Debêntures",
    value: "Debenture",
  },
];

const showReports = (reports: IncomeTaxReport[]) =>
  reports.map(({ year, file_url }) => {
    return (
      <Alert
        key={year}
        level="info"
        notDismissable
        message={
          <span>
            <Link to={file_url} target="_blank">
              Clique aqui
            </Link>{" "}
            para acessar o documento.
          </span>
        }
      />
    );
  });

const IncomeTax: React.FC = () => {
  const [currentYear, setCurrentYear] = useState<string>();
  const [currentInvestableType, setCurrentInvestableType] =
    useState<StatusValue>();

  const isOpportunity = currentInvestableType == "Opportunity";

  const { data: fetchYears } = useApiQuery<FetchYears>([
    "income_reports",
    { params: { fetch_years: true } },
  ]);

  const availableYears =
    fetchYears?.[currentInvestableType as keyof FetchYears];

  const { data: incomeReports } = useApiQuery<IncomeTaxReport[]>(
    [
      "income_reports",
      {
        params: {
          q: {
            year_eq: currentYear,
            investable_type_eq: currentInvestableType,
          },
        },
      },
    ],
    {
      enabled: !!currentYear && availableYears.includes(Number(currentYear)),
    }
  );

  const { data: capitalGainReports } = useApiQuery<IncomeTaxReport[]>(
    ["capital_gain_reports", { params: { q: { year_eq: currentYear } } }],
    {
      enabled: !!currentYear && isOpportunity,
    }
  );

  useEffect(() => {
    fetchYears && setCurrentInvestableType(Object.keys(fetchYears)[0]);
  }, [fetchYears]);

  useEffect(() => {
    availableYears && setCurrentYear(availableYears[0].toString());
  }, [currentInvestableType]);

  return (
    <Page
      crumbs={[{ title: "Imposto de Renda" }]}
      title="Imposto de Renda"
      headerButtons={
        <>
          <SelectStatus
            data={investableTypes}
            selected={currentInvestableType}
            onSelect={setCurrentInvestableType}
            disableItem={(item) =>
              fetchYears &&
              !Object.keys(fetchYears).includes(item as keyof FetchYears)
            }
          />

          {currentYear && (
            <Dropdown
              initialValue={currentYear}
              options={availableYears.map((year) => {
                const value = year.toString();
                return { value, label: value };
              })}
              onValueChange={setCurrentYear}
            >
              Ano-Calendário:
              <b>{currentYear}</b>
              <i>
                <SvgArrow />
              </i>
            </Dropdown>
          )}
        </>
      }
    >
      <InstructionsBoard currentInvestableType={currentInvestableType} />

      {incomeReports?.length > 0 && (
        <Card header={`Informe de Rendimentos (${currentYear})`}>
          {showReports(incomeReports)}
        </Card>
      )}

      {isOpportunity && capitalGainReports?.length > 0 && (
        <Card header={`Ganho de Capital (${currentYear})`}>
          {showReports(capitalGainReports)}
        </Card>
      )}
    </Page>
  );
};

export default IncomeTax;
